import { Typography, Avatar } from 'antd'
import i18n from 'i18next'

import { LinkToResourceWrapper } from '@/Core/Components/Resource'
import { Form, FormField } from '@/Core/UI/Form'
import { cx } from '@/Ship/Helpers/cssClass'

import styles from '../Components/EntitreeFlexTree/CustomNode.module.css'

import type { DepartmentUser } from '../Contracts/DepartmentTree.types'
import type { FormStore } from '@/Core/UI/Form/Stores/FormStore'

interface UsersInfoContainerProps {
    users?: DepartmentUser[] | undefined
    label: string
}

// Core's form field is untyped; depFn receives the form store and the field instance.
/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
interface DepFnField {
    setIsRequired: (required: boolean) => void
    mergeInputProps: (props: Record<string, unknown>) => void
}
/* eslint-enable no-unused-vars */

interface DepFnContext {
    formContext: FormStore
    field: DepFnField
}

export const UsersInfoContainer = ({ users = [], label }: UsersInfoContainerProps) => {
    if (!users.length) {
        return null
    }

    return (
        <div style={{ marginBottom: '15px' }}>
            <Typography.Paragraph>{label}</Typography.Paragraph>

            <div className={cx(styles, 'userInfoWrapper')}>
                {users.map((user) => {
                    const position = typeof user.position === 'string' ? user.position : user.position?.title
                    return (
                        <LinkToResourceWrapper
                            key={user.id}
                            entityId={user.id}
                            {...(user['@type'] ? { entityType: user['@type'] } : {})}
                        >
                            <div className={cx(styles, 'userInfoContainer')}>
                                <Avatar
                                    className={cx(styles, 'avatar')}
                                    src={user.avatar?.fileUrl ?? '/assets/images/no-profile-picture.png'}
                                />
                                <div className={cx(styles, 'info')}>
                                    <Typography.Text className={cx(styles, 'name')}>
                                        {user.fullName || '‒'}
                                    </Typography.Text>
                                    <Typography.Text className={cx(styles, 'position')}>
                                        {position || '‒'}
                                    </Typography.Text>
                                </div>
                            </div>
                        </LinkToResourceWrapper>
                    )
                })}
            </div>
        </div>
    )
}

export const DepartmentFormTemplate = ({ formContext }: { formContext: FormStore }) => {
    const isEditable = formContext.isEditable
    const values = formContext.values

    return (
        <Form formContext={formContext}>
            <FormField
                name="parent"
                // Core's FieldConfig.depFn is `(context?: any) => boolean | void`; the FormField
                // prop narrows it to `() => boolean`, so the callback is cast to match.
                depFn={
                    (({ formContext, field }: DepFnContext): boolean | void => {
                        if (formContext.values['code'] === 'root') return false

                        field.setIsRequired(true)
                    }) as unknown as () => boolean
                }
            />
            <FormField name="title" />
            <FormField
                name="code"
                depFn={
                    (({ formContext, field }: DepFnContext): boolean | void => {
                        if (formContext.values['code'] === 'root') field.mergeInputProps({ disabled: true })
                    }) as unknown as () => boolean
                }
            />
            <FormField name="isActive" />
            {isEditable ? (
                <FormField name="chiefs" />
            ) : (
                <UsersInfoContainer
                    users={values['chiefs'] as DepartmentUser[] | undefined}
                    label={i18n.t('Heads', { ns: 'Setting.Department' })}
                />
            )}
            {!isEditable && (
                <UsersInfoContainer
                    users={values['users'] as DepartmentUser[] | undefined}
                    label={i18n.t('Users', { ns: 'Setting.Department' })}
                />
            )}
        </Form>
    )
}
